<template>
  <div>
    <div class="ml-2 mt-12 text-h5">グループ管理</div>
    <div class="ml-4 blue-grey--text">赤印のある項目は必須入力です</div>
    <alert :errors="errors" css_class="ma-1"></alert>
    <v-container style="max-width: 800px" v-show="view_status.editable">
      <v-row
        v-for="input in inputs"
        :key="input.key"
        no-gutters
        dense
        class="mb-2"
      >
        <v-col cols="4">
          <v-subheader class="title mr-2" style="height: 100%"
            >{{ input.title
            }}<v-icon class="ml-1 mb-3 red--text caption" v-if="input.required"
              >mdi-asterisk</v-icon
            ></v-subheader
          ></v-col
        >
        <v-col>
          <v-text-field
            v-if="input.key == 'group-name'"
            outlined
            dense
            hide-details="auto"
            :label="input.title"
            v-model.trim="$v.formData.groupName.$model"
            :error-messages="groupNameErrorMessage"
            :error-count="3"
          ></v-text-field>
          <v-select
            v-if="input.key == 'parent-group'"
            :items="parentList"
            v-model="parentGroup"
            class="fixed-width-500"
            dense
            hide-details
            return-object
            outlined
          ></v-select>
          <div v-if="input.key == 'parent-group.fixed'" class="mt-2 mb-2 ml-3">
            {{ parentGroup.text }}
          </div>
          <append-two-column-select-list
            ref="permissionSelect"
            v-if="input.key == 'group-permission'"
            :contents="permission"
            :roles="permissionRoles"
            :min="0"
            :appendable="true"
          ></append-two-column-select-list>
        </v-col>
      </v-row>
    </v-container>

    <v-container style="max-width: 800px">
      <v-row justify="center" style="text-align: center">
        <v-col cols="6">
          <v-btn
            color="secondary"
            outlined
            class="ma-2"
            width="200"
            @click="cancel"
            >キャンセル</v-btn
          >
        </v-col>
        <v-col cols="6">
          <v-btn
            color="primary"
            class="ma-2"
            width="200"
            @click="register"
            :disabled="!view_status.editable"
            >登録</v-btn
          >
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import AppendTwoColumnSelectList from "../components/common/append-two-column-select-list.vue";
import Api from "@/logic/api/api.js";
import Validate from "@/logic/common/validate.js";
import Roles from "@/logic/common/roles.js";
import Alert from "../components/common/alert.vue";
import AlertBuilder from "../logic/common/alert-contents";
import { required, maxLength } from "vuelidate/lib/validators";
import valueMapper from "../logic/common/value-mapper";
import ValueConverer from "../logic/common/value-converter";

export default {
  components: {
    "append-two-column-select-list": AppendTwoColumnSelectList,
    alert: Alert,
  },
  validations: {
    formData: {
      groupName: {
        required,
        maxLength: maxLength(64),
      },
    },
  },
  computed: {
    disableRegisterButton() {
      return Validate.hasError([this.$v.formData.groupName]);
    },
    groupNameErrorMessage() {
      return Validate.buildLocalizedErrorMessage(
        this.$v.formData.groupName,
        "グループ名",
        ["required", "maxLength"],
        !this.delayCheck
      );
    },
  },
  data() {
    // URLから参照先を取得する
    const uid = this.$route.params.uid;
    const isCreate = uid == "create" ? true : false;

    // 画面に表示する項目
    const inputs = [];
    inputs.push({
      key: "group-name",
      title: "グループ名",
      required,
    });
    if (isCreate) {
      inputs.push({
        key: "parent-group",
        title: "親グループ",
        required,
      });
    } else {
      inputs.push({
        key: "parent-group.fixed",
        title: "親グループ",
      });
    }
    inputs.push({
      key: "group-permission",
      title: "グループ権限",
    });

    this.$nextTick(() => {
      this.loadData(uid);
    });
    return {
      view_status: {
        editable: false,
      },
      isCreate: isCreate,
      groupId: uid,
      permission: [],
      permissionRoles: Roles.groupToGroupRoles(),
      inputs: inputs,
      delayCheck: false,
      parentGroup: {
        text: "-",
        value: "",
      },
      parentList: [],
      formData: {
        groupName: "",
      },
      updateData: {
        recordDate: "",
        permissionIds: [],
      },
      errors: [],
    };
  },
  methods: {
    /**
     * グループ情報を取得、画面に表示する
     */
    loadData(uid) {
      this.view_status.editable = false;
      this.$store.commit("setWaiting", true);
      this.$nextTick(() => {
        // 除外リストに含まれないグループをフィルタする
        // @param group_list : 対象のグループリスト
        // @param ignore_list : 除外リスト
        const filterPermissionListData = (group_list, ignore_list) => {
          return group_list.filter(
            (group) => !ignore_list.includes(group.group_id)
          );
        };
        // グループ情報を反映する
        const displayGroupData = (group) => {
          // グループ名を設定する
          this.formData.groupName = group.display_name;
          // 親グループを設定する
          this.parentGroup = {
            text: group.parent_group.display_name,
            value: group.parent_group.group_id,
          };
          // 編集者に管理権限のないグループ権限を表示する
          this.$refs.permissionSelect[0].setFixedContents(
            Roles.convertPermissionData(
              group.permissions,
              this.permission,
              () => true,
              false,
              () => true
            )
          );
          // 編集者に管理権限のあるグループ権限を表示する
          this.$refs.permissionSelect[0].setSelected(
            Roles.convertPermissionData(
              group.permissions,
              this.permission,
              () => true,
              true,
              () => true
            )
          );
          // 編集前の権限情報を保持する
          this.updateData.permissionIds = group.permissions;
          // 更新情報を保持する
          this.updateData.recordDate = group.update_date;
        };
        const mapping = [];
        const process = [];

        // グループ情報を取得する
        if (uid != "create") {
          mapping.push("group.detail");
          process.push(
            Api.call("/admin/group", {
              purpose: "GROUP_DETAIL",
              group_id: uid,
            })
          );
        }
        // 親子を含むグループ名の一覧を取得する
        mapping.push("permission.tree");
        process.push(
          Api.call("/admin/group", {
            purpose: "GROUP_TREE",
            no_detail: true,
          })
        );
        // 管理者権限のあるグループ名の一覧を取得する
        mapping.push("permission.list");
        process.push(
          Api.call("/admin/group", {
            purpose: "GROUP_MANAGEMENT",
            no_detail: true,
          })
        );
        // 必要なAPIを全て実行する
        Promise.all(process)
          .then((result) => {
            this.$store.commit("setWaiting", false);
            let group_data = null;
            // グループ情報を取得する
            if (mapping.includes("group.detail")) {
              group_data =
                result[mapping.indexOf("group.detail")].group_list[0];
            }
            if (mapping.includes("permission.tree")) {
              // 編集対象のグループを除いた、管理権限のあるグループとその子グループを権限の付与対象とする
              this.permission = ValueConverer.groupForSelectbox(
                filterPermissionListData(
                  result[mapping.indexOf("permission.tree")].group_list,
                  [uid]
                ),
                this.$store.getters.userGroupIds
              );
            }
            if (mapping.includes("permission.list")) {
              if (group_data == null) {
                // 編集対象のグループを除いた、管理権限のあるグループを対象とする
                this.parentList = ValueConverer.groupForSelectbox(
                  filterPermissionListData(
                    result[mapping.indexOf("permission.list")].group_list,
                    [uid]
                  ),
                  this.$store.getters.userGroupIds
                );
                // 親グループの初期選択位置を設定
                this.parentGroup = this.parentList[0];
              }
            }
            if (group_data != null) {
              // 編集対象のグループ情報を表示する
              displayGroupData(group_data);
            }
            this.view_status.editable = true;
          })
          .catch((result) => {
            // エラーになったリクエストが一つでもあればここに入る
            this.$store.commit("setWaiting", false);
            const message = valueMapper
              .map({
                403: AlertBuilder.error("権限がありません"),
              })
              .getOrElse(result.status, AlertBuilder.invalid());
            if (message.isInvalid) {
              Api.resolve(result);
            } else {
              this.showMessage(message);
            }
          });
      });
    },
    /**
     * メッセージを初期化する
     */
    clearMessage() {
      this.errors = [];
    },
    /**
     * メッセージを表示する
     */
    showMessage(error) {
      this.errors.push(error);
      window.scrollTo(0, 0);
    },
    /**
     * 登録データを作成する
     */
    sendBaseData() {
      return {
        display_name: this.formData.groupName,
        permissions: this.$refs.permissionSelect[0]
          .getAllContents()
          .map((item) => {
            return {
              ...item.role.value,
              destination_id: item.name.value,
              type: "permission",
            };
          }),
      };
    },
    /**
     * 登録データを作成する
     */
    buildRegisterData() {
      return {
        ...this.sendBaseData(),
        parent_group_id: this.parentGroup.value,
      };
    },
    /**
     * 更新データを作成する
     */
    buildUpdateData() {
      let result = {
        ...this.sendBaseData(),
        group_id: this.groupId,
        original_permission_ids: this.updateData.permissionIds.map((item) => {
          return {
            permission_id: item.permission_id,
          };
        }),
        update_date: this.updateData.recordDate,
      };
      result["permissions"] = this.$refs.permissionSelect[0]
        .getAllContents()
        .map((item) => {
          let result = {
            ...item.role.value,
            destination_id: item.name.value,
            type: "permission",
            is_update: item.meta.edit,
          };
          if (item.meta.permission_id) {
            result["permission_id"] = item.meta.permission_id;
          }
          return result;
        });
      return result;
    },
    /**
     * データを登録する
     */
    register() {
      this.delayCheck = true;
      this.clearMessage();
      // 入力を検証する
      if (this.disableRegisterButton) {
        this.showMessage(AlertBuilder.error("入力した項目が不正です"));
        return;
      }
      if (this.$refs.permissionSelect[0].hasError()) {
        this.showMessage(AlertBuilder.error("グループ権限に重複があります"));
        return;
      }
      // リクエストパラメータを作成する
      let updateUrl = "";
      let postData = null;
      if (this.isCreate) {
        updateUrl = "/admin/group/create";
        postData = this.buildRegisterData();
      } else {
        updateUrl = "/admin/group/update";
        postData = this.buildUpdateData();
      }
      // APIを実行する
      this.$store.commit("setWaiting", true);
      Api.call(updateUrl, postData)
        .then(() => {
          this.$store.commit("setWaiting", false);
          this.$router.push("/group");
        })
        .catch((error) => {
          this.$store.commit("setWaiting", false);
          const message = valueMapper
            .map({
              401: AlertBuilder.invalid(),
              409: AlertBuilder.error("編集が競合しています"),
            })
            .getOrElse(error.status, AlertBuilder.error("更新に失敗しました"));
          if (message.isInvalid) {
            Api.resolve(error);
          } else {
            this.showMessage(message);
          }
        });
    },
    cancel() {
      this.$router.push("/group");
    },
  },
};
</script>

<style scoped>
.title {
  background-color: #999999;
  color: white;
}
</style>
